import { useEffect, useMemo, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useReactiveVar } from '@apollo/client'
import { Box, Button, TextField } from '@mui/material'
import moment from 'moment'
import { titleName, toNumber, toSentenceCase } from 'utils/helperFns'
import { setAlertQueue } from 'cache/alerts'
import { dealLoadStatusVar, resetDealLoadStatus } from 'cache/deal-load-status'
import { getDealCancelled, setIsDealLocked } from 'cache/deal-status'
import { useGetPendingActionsDecisionStatus } from 'cache/decision-status'
import { getShouldDisableActionBtns } from 'cache/form-valid'
import { latestDealVar, useDealInfo } from 'cache/latest-deal-info'
import { getUserLockStatus } from 'cache/user-profile'
import DialogModal from 'components/DialogModal/DialogModal'
import ErrorPageURL from 'components/ErrorPage/ErrorPageURL'
import Loader from 'components/Loader/Loader'
import { ENABLED_DEAL_STATUSES } from 'constants/appConstants'
import { useLockDeal } from 'hooks/use-deal-lock'
import { useUnlockDeal } from 'hooks/use-deal-unlock'
import { useSaveDecision } from 'hooks/use-decision'
import { useGetEditPageDetails } from 'hooks/use-get-edit-page-details'
import { useUnlockDealOnUnload } from 'hooks/use-unlock-deal-on-unload'
import { getUserProfile, useGuidanceRole, useUserProfile } from 'hooks/use-user-profile'
import useWorkflowParams from 'hooks/use-workflow-params'
import { useStyles } from 'styles/material-styles/decisionComponent'
import { MessageType } from 'ts/enums/messageType.enums'
import { DecisionComponentInterface } from 'ts/interfaces/decisionComponent.interface'

interface PendingActionType {
  reasonCode?: string
  closeReasonCode?: string
  approvalFlowLevel?: number
  decisionActionType?: string
  isCurrentUser?: boolean
  approvalStatusType?: string
  approvalStatusComment?: string
  employeeNumber?: number
  dealId?: number
}

const DecisionComponent = ({
  isActiveApprover: _isActiveApprover,
  emailContent,
  emailContentLoading,
  version,
  dealInfo,
  emailTemplateId,
}: DecisionComponentInterface) => {
  const { classes } = useStyles()
  const { decisionReason } = useWorkflowParams()
  const { dealDates } = useDealInfo()
  const history = useHistory()
  const formattedDealEndDate = moment(dealDates?.dealDate?.endDate?.toString())
    .utc()
    .format('DD.MM.YYYY')
  const [openModal, setOpenModal] = useState(false)
  const [openModalTitle, setOpenModalTitle] = useState('')
  const [commentValue, setCommentValue] = useState('')

  const { unlockDealMutation } = useUnlockDeal()

  const { authBenchmarkFL, authAdjustmentPCT } = useGuidanceRole()
  const { lockDealMutation } = useLockDeal()
  const { empId, userId } = getUserProfile()
  const { userEmail } = useUserProfile()
  const latestDealData = useReactiveVar(latestDealVar)
  const pendingActionsDecisionData = useGetPendingActionsDecisionStatus()
  const dealLoadStatus = useReactiveVar(dealLoadStatusVar)

  const { prewarmEditPage } = useGetEditPageDetails()
  const prewarmResultRef = useRef<{ success: boolean; errorMessage?: string } | null>(null)
  const prewarmInFlightRef = useRef<Promise<{ success: boolean; errorMessage?: string }> | null>(
    null
  )
  const isNavigatingToEditPageRef = useRef(false)
  const [isNavigating, setIsNavigating] = useState(false)

  const pendingAction = useMemo<PendingActionType>(() => {
    const action = pendingActionsDecisionData?.find((item) => {
      return item?.reasonCode === decisionReason
    })
    return action ?? {}
  }, [decisionReason, pendingActionsDecisionData])

  const isDecisionReasonCurrentUser = pendingAction?.isCurrentUser
  const actionButtonsDisabled = getShouldDisableActionBtns()
  const isDealExpired = dealInfo?.isDealExpired
  const isFreezeFlag = dealInfo?.freezeFlag === 'Y'
  const isLatestEmail = dealInfo?.bdId !== version
  const isDealCancelled = getDealCancelled()
  const isDealLocked = getUserLockStatus()
  const isDealLoadDisabled = dealLoadStatus
    ? dealLoadStatus.didError ||
      !dealLoadStatus.isCurrentVersionDeal ||
      !ENABLED_DEAL_STATUSES.includes(dealLoadStatus.dealStatus)
    : true

  const isButtonDisable =
    !pendingAction?.reasonCode ||
    !isDecisionReasonCurrentUser ||
    pendingAction?.decisionActionType !== 'DECIDE' ||
    actionButtonsDisabled

  const isEditButtonDisable = isButtonDisable || isDealLoadDisabled

  const handleOpen = (inputValue: string) => {
    setOpenModalTitle(inputValue)
    setOpenModal(true)
  }

  const handleClose = () => {
    setOpenModal(false)
    setOpenModalTitle('')
    setCommentValue('')
  }
  const { saveComment, loading } = useSaveDecision(setCommentValue, handleClose)

  const handleOnApply = async () => {
    if (pendingAction && latestDealData?.dealId) {
      await saveComment({
        variables: {
          input: {
            dealId: latestDealData.dealId,
            reasonCode: pendingAction?.reasonCode ?? '',
            closeReasonCode: pendingAction?.closeReasonCode ?? '',
            employeeNumber: toNumber(empId) ?? 0,
            approvalStatusType: toSentenceCase(openModalTitle),
            approvalStatusComment: commentValue,
            approvalFlowLevel: pendingAction?.approvalFlowLevel,
            authBenchmark: authBenchmarkFL,
            adjustmentPercentage: authAdjustmentPCT,
          },
        },
      })
      if (userId) {
        await unlockDealMutation(latestDealData.dealId)
      }
    }
  }

  const manageClick = () => {
    const backupUrl = process.env.REACT_APP_BACKUP_URL
    if (window.location.hostname === 'localhost') {
      const fallbackUrl = 'http://localhost:3000/manage-backup'
      window.open(fallbackUrl, '_blank', 'noopener,noreferrer')
    } else {
      window.open(backupUrl, '_blank', 'noopener,noreferrer')
    }
  }

  // Trigger prewarm in the background as soon as the Edit button is enabled and dealId is available,
  // so clicking Edit navigates immediately without waiting for the API.
  useEffect(() => {
    const dealId = latestDealData?.dealId
    if (isEditButtonDisable || !dealId) return

    // Skip if a successful result is already cached or a call is already in-flight.
    if (prewarmResultRef.current?.success || prewarmInFlightRef.current) return

    prewarmInFlightRef.current = prewarmEditPage(dealId, userEmail)
    prewarmInFlightRef.current.then((result) => {
      prewarmResultRef.current = result
      prewarmInFlightRef.current = null
    })
  }, [isEditButtonDisable, latestDealData?.dealId, prewarmEditPage, userEmail])

  const handleEditClick = async () => {
    const dealId = latestDealData?.dealId
    if (!dealId) return

    let result: { success: boolean; errorMessage?: string }

    if (prewarmResultRef.current?.success) {
      // Background call already succeeded — navigate immediately.
      result = prewarmResultRef.current
    } else if (prewarmInFlightRef.current) {
      // Background call is still in progress — show loader and wait for it.
      setIsNavigating(true)
      result = await prewarmInFlightRef.current
      setIsNavigating(false)
    } else {
      // Background call failed or never ran — trigger one API call now.
      // Set prewarmInFlightRef.current before awaiting so the background useEffect
      // guard sees it and does not start a duplicate parallel request.
      prewarmInFlightRef.current = prewarmEditPage(dealId, userEmail)
      result = await prewarmInFlightRef.current
      prewarmInFlightRef.current = null
    }

    if (result.success) {
      // Reset deal load status and prewarm refs before navigating so that on return
      // the button stays disabled until a fresh GetDealLoadStatus response arrives,
      // preventing the prewarm from firing against stale cached state.
      resetDealLoadStatus()
      prewarmResultRef.current = null
      prewarmInFlightRef.current = null
      // Signal cleanup to skip unlock — deal stays locked for the edit page.
      isNavigatingToEditPageRef.current = true
      sessionStorage.setItem('fromDecisionPage', 'true')
      history.push(`${window.location.pathname}/edit`, { fromDecisionPage: true })
    } else {
      setAlertQueue({
        type: MessageType.ERROR,
        message: result.errorMessage ?? 'Failed to load edit page',
      })
    }
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const comment = event.target.value
    setCommentValue(comment)
  }

  useEffect(() => {
    const dealId = latestDealData?.dealId
    if (isButtonDisable || !dealId) return

    const lockDeal = async () => {
      try {
        const result = await lockDealMutation(dealId)
        if (result?.error) {
          console.error('Failed to lock deal:', result.error)
        } else {
          setIsDealLocked(true)
        }
      } catch (error) {
        console.error('Failed to lock deal:', error)
      }
    }
    lockDeal()

    // Unlock on SPA navigation; skip when going to edit page so the deal stays locked.
    return () => {
      if (!isNavigatingToEditPageRef.current) {
        unlockDealMutation(dealId)
        setIsDealLocked(false)
      }
      isNavigatingToEditPageRef.current = false
    }
  }, [isButtonDisable, latestDealData?.dealId, lockDealMutation, unlockDealMutation])

  useUnlockDealOnUnload({ dealId: latestDealData?.dealId, userId, enabled: !isButtonDisable })

  return (
    <Box className={classes.mainContainer}>
      <Box>
        <Box className={classes.container}>
          <Box className={classes.title}>Decision Page</Box>
          <ErrorPageURL />
          <Box className={classes.content}>
            <Box className={classes.buttonContainer}>
              <Button
                variant="contained"
                color="primary"
                className={classes.primaryButton}
                onClick={() => handleOpen('ACCEPT')}
                disabled={isButtonDisable}
              >
                Accept
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                className={classes.secondaryButton}
                onClick={() => handleOpen('REVISE')}
                disabled={isButtonDisable}
              >
                Revise
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                className={classes.secondaryButton}
                onClick={() => handleOpen('RESTART')}
                disabled={isButtonDisable}
              >
                Restart
              </Button>
              <Box className={classes.manageBackupLink}>
                <button className={classes.link} onClick={manageClick} type="button">
                  MANAGE BACKUP
                </button>
              </Box>
              <Button
                variant="outlined"
                color="secondary"
                className={classes.secondaryButton}
                onClick={handleEditClick}
                disabled={isEditButtonDisable}
              >
                Edit
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
      {!(isDealExpired || isDealCancelled || isDealLocked || isFreezeFlag || isLatestEmail) && (
        <Box className={classes.emailContentContainer}>
          <div
            className={classes.emailContentBox}
            dangerouslySetInnerHTML={{ __html: emailContent }}
          />
        </Box>
      )}
      {!(isDealCancelled || isFreezeFlag) && (
        <Box className={classes.footerContainer}>
          <span className={classes.templateId}>Template ID:{emailTemplateId || 'N/A'}</span>
          <span className={classes.dealEndDate}>Deal End Date:{formattedDealEndDate || 'N/A'}</span>
        </Box>
      )}
      <DialogModal
        open={openModal}
        onClose={handleClose}
        title={titleName(openModalTitle)}
        commentValue={commentValue}
        onApply={handleOnApply}
        isApplyDisabled={commentValue?.length === 0}
        applyButtonText="Apply"
        cancelButtonText="Cancel"
      >
        <TextField
          placeholder="Please insert a comment"
          variant="outlined"
          multiline
          rows={10}
          fullWidth
          onChange={handleInputChange}
          value={commentValue}
          className={classes.textField}
        />
      </DialogModal>
      <Loader open={loading || emailContentLoading || isNavigating} />
    </Box>
  )
}

export default DecisionComponent
